"use client";

import React, { useEffect, useRef, useState } from "react";
import { BookOpen, Calendar, Sparkles, ChartLine, GraduationCap, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const steps = [{
  num: "01",
  title: "Expert Counseling",
  desc: "Begin your journey with a strategic assessment of your academic history and future career aspirations.",
  icon: BookOpen,
  emphasized: false
}, {
  num: "02",
  title: "Strategic Selection",
  desc: "Our consultants curate a tailored list of global universities that perfectly match your profile and budget.",
  icon: Calendar,
  emphasized: false
}, {
  num: "03",
  title: "Application Mastery",
  desc: "From compelling SOPs to meticulous documentation, we ensure your application stands out to admissions committees.",
  icon: Sparkles,
  emphasized: true
}, {
  num: "04",
  title: "Visa & Scholarships",
  desc: "Navigate the complexities of financial documentation and embassy interviews with our expert visa success team.",
  icon: ChartLine,
  emphasized: false
}, {
  num: "05",
  title: "Global Transition",
  desc: "Prepare for your new life with pre-departure briefings covering accommodation, travel, and campus integration.",
  icon: GraduationCap,
  emphasized: false
}];
interface TimelineItemProps {
  step: typeof steps[0];
  index: number;
  "data-index": number;
  lineProgress: number;
}
function TimelineItem({
  step,
  index,
  "data-index": dataIndex,
  lineProgress
}: TimelineItemProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const isEven = index % 2 === 0;
  const itemPosition = index / (steps.length - 1) * 100;
  const isActive = lineProgress >= itemPosition - 10;
  return <div data-section-id="96483" data-index={dataIndex} ref={ref} className={`relative flex flex-col md:flex-row items-center justify-between w-full mb-20 last:mb-0 ${isEven ? "md:flex-row-reverse" : ""}`}>
      {}
      <div className="hidden md:block md:w-[45%]" />

      {}
      <div className="absolute left-6 md:left-1/2 top-0 md:top-1/2 -translate-x-1/2 md:-translate-y-1/2 z-10 flex flex-col items-center justify-center">
        <div className={`flex items-center justify-center rounded-full border-4 transition-all duration-500 ${step.emphasized ? "w-20 h-20 md:w-24 md:h-24 bg-background" : "w-12 h-12 md:w-16 md:h-16 bg-background"} ${isActive ? "border-secondary shadow-[0_0_30px_var(--color-secondary)]" : "border-muted shadow-none"}`}>
          <span className={`font-default font-bold transition-colors duration-500 ${step.emphasized ? "text-2xl md:text-3xl" : "text-lg md:text-xl"} ${isActive ? "text-primary" : "text-muted-foreground"}`}>
            {step.num}
          </span>
        </div>
      </div>

      {}
      <div className={`w-full pl-20 md:pl-0 md:w-[45%] transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0 md:translate-x-0" : `opacity-0 translate-y-12 ${isEven ? "md:-translate-x-12" : "md:translate-x-12"}`}`}>
        <div className={`p-6 md:p-8 rounded-lg border backdrop-blur-md transition-all duration-500 ${isActive ? "bg-card/80 border-secondary/30 shadow-[0_20px_50px_rgba(15,23,42,0.08)]" : "bg-card/40 border-border/50 shadow-sm"} ${step.emphasized ? "ring-1 ring-secondary/20" : ""}`}>
          <div className="flex items-center gap-4 mb-4">
            <div className={`p-3 rounded-lg ${isActive ? "bg-secondary/10 text-secondary" : "bg-muted text-muted-foreground"} transition-colors duration-500`}>
              <step.icon className={step.emphasized ? "w-8 h-8" : "w-6 h-6"} />
            </div>
            <h3 className="text-xl md:text-2xl font-bold font-default tracking-tight text-primary">
              {step.title}
            </h3>
          </div>
          <p className="text-muted-foreground leading-relaxed text-sm md:text-base font-default">
            {step.desc}
          </p>
        </div>
      </div>
    </div>;
}
export default function PathToSuccess() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleScroll = () => {
      if (!timelineRef.current) return;
      const rect = timelineRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const startPoint = windowHeight / 2;
      const scrolledPastStart = startPoint - rect.top;
      const totalDistance = rect.height;
      let progress = scrolledPastStart / totalDistance * 100;
      progress = Math.max(0, Math.min(100, progress));
      setScrollProgress(progress);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section id="the-briviox-roadmap" ref={sectionRef} className="bg-background text-foreground py-24 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-secondary/5 rounded-full blur-[120px] pointer-events-none" />
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {}
        <div className="text-center max-w-3xl mx-auto mb-20 md:mb-32">
          <h2 className="text-sm font-bold tracking-wider text-secondary uppercase mb-4 font-default">
            ADMISSIONS STRATEGY
          </h2>
          <h3 className="text-4xl md:text-5xl lg:text-6xl font-bold font-default tracking-tight mb-6 text-primary">
            The Briviox <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Roadmap</span>
          </h3>
          <p className="text-lg text-muted-foreground font-default">
            Where ambition meets achievement. Follow our proven methodology to secure your place at the world's leading academic institutions.
          </p>
        </div>

        {}
        <div className="max-w-5xl mx-auto relative" ref={timelineRef}>
          {}
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-1 bg-muted -translate-x-1/2 rounded-full" />
          
          {}
          <div className="absolute left-6 md:left-1/2 top-0 w-1 bg-secondary -translate-x-1/2 rounded-full transition-all duration-300 ease-out shadow-[0_0_15px_var(--color-secondary)]" style={{
          height: `${scrollProgress}%`
        }} />

          {}
          <div className="relative py-10">
            {steps.map((step, i) => <TimelineItem key={i} data-index={i} index={i} step={step} lineProgress={scrollProgress} />)}
          </div>
        </div>

        {}
        <div className="mt-20 text-center flex justify-center">
          <Button size="lg" className="rounded-lg px-8 py-6 text-lg font-semibold bg-gradient-to-r from-primary to-secondary text-white shadow-[0_0_20px_rgba(37,99,235,0.3)] hover:shadow-[0_0_30px_rgba(37,99,235,0.5)] transition-all">
            Consult Our Experts
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </div>
    </section>;
}